import { useEffect, useMemo } from 'react';
import { useForm, useWatch, type FieldPath } from 'react-hook-form';

import { useAppSelector, useTranslate } from '@src/core/hooks';
import type { PaymentOption } from '@src/domains/payments';
import type { Transactable } from '@src/domains/transactables';
import { buyerIsForeign, useBuyerCountryCode } from '@src/features/customers';
import { useProcessPayment } from '@src/features/payments/hooks/useProcessPayment';

import { buildCardFormDefaults } from '../../core/buildCardFormDefaults';
import { cardPaymentFormSchema, type CardFormOutput, type CardFormValues } from '../../core/cardPaymentFormSchema';
import { mapPaymentErrorsToFormFields } from '../../core/mapPaymentErrorsToFormFields';
import { resolveCardFormVisibility } from '../../core/resolveCardFormVisibility';
import { transformingZodResolver } from '../../core/transformingZodResolver';
import { useBillingAddressLookup } from '../../hooks/useBillingAddressLookup';

type UseCardPaymentFormInput = {
  paymentOption: PaymentOption;
  transactable: Transactable;
  onPaid?: () => void;
};

export const useCardPaymentForm = ({ paymentOption, transactable, onPaid }: UseCardPaymentFormInput) => {
  const { i18n } = useTranslate();
  const isForeign = useAppSelector(buyerIsForeign);
  const buyerCountryCode = useBuyerCountryCode();
  const { pay, submitting, errors: contextErrors } = useProcessPayment(transactable);

  const schema = useMemo(
    () => cardPaymentFormSchema({ t: i18n, isForeign, buyerCountryCode, paymentOption }),
    [i18n, isForeign, buyerCountryCode, paymentOption],
  );

  const visibility = useMemo(() => resolveCardFormVisibility({ paymentOption, isForeign }), [paymentOption, isForeign]);

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
  } = useForm<CardFormValues, unknown, CardFormOutput>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: transformingZodResolver(schema),
    defaultValues: buildCardFormDefaults({ paymentOption, isForeign }),
  });

  const values = useWatch({ control });
  const { lookingUpAddress, lookUpAddress } = useBillingAddressLookup({
    enabled: visibility.showBillingAddress,
    isForeign,
    setValue,
  });

  const canSubmit = useMemo(() => {
    return schema.safeParse(values).success;
  }, [values]);

  useEffect(() => {
    mapPaymentErrorsToFormFields(contextErrors, visibility).forEach(({ path, message }) =>
      setError(path as FieldPath<CardFormValues>, { type: 'manual', message }),
    );
  }, [contextErrors, setError, visibility]);

  const submit = handleSubmit(async (card: CardFormOutput) => {
    const result = await pay(paymentOption.paymentMethod.id, card);

    if (result?.success) {
      onPaid?.();
    }
  });

  return {
    control,
    errors,
    visibility,
    installmentOptions: paymentOption.installmentOptions,
    canSubmit: canSubmit,
    submitting,
    lookingUpAddress,
    lookUpAddress,
    submit,
  };
};

export default useCardPaymentForm;
